import type { GeneralRuleKey, SiteDto, UserDto } from '@app/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { fetchSites } from '../../admin-sites/api';
import { useAuthStore } from '../../auth/store';
import {
  assignUserToSite,
  disableUser,
  enableUser,
  fetchAdminUsers,
  fetchUserGeneralRules,
  fetchUserSites,
  setPreferredSiteForUser,
  unassignUserFromSite,
  updateUserGeneralRules,
  updateUserRole,
} from '../api';
import { RoleSelect } from './RoleSelect';
import { SitePicker } from './SitePicker';

export const USERS_KEY = ['admin-users'] as const;

const ROLE_LABELS: Record<UserDto['role'], string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  member: 'Membre',
};

const RULE_LABELS: Record<GeneralRuleKey, string> = {
  documentCreate: 'Créer',
  documentDisable: 'Archiver',
  userCreate: 'Créer',
  userDisable: 'Désactiver',
  processCreate: 'Créer',
  processDisable: 'Archiver',
  structureProcessCreate: 'Créer',
  structureProcessDisable: 'Archiver',
};

const RULE_GROUPS: { label: string; keys: GeneralRuleKey[] }[] = [
  { label: 'Documents', keys: ['documentCreate', 'documentDisable'] },
  { label: 'Utilisateurs', keys: ['userCreate', 'userDisable'] },
  { label: 'Processus', keys: ['processCreate', 'processDisable'] },
  { label: 'Structure', keys: ['structureProcessCreate', 'structureProcessDisable'] },
];

/* ─── Chip toggle ─────────────────────────────────────────── */
function Chip({
  active,
  disabled,
  onClick,
  children,
  color = 'teal',
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  color?: 'teal' | 'indigo';
}) {
  const activeStyle =
    color === 'indigo'
      ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
      : 'border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100';
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors
        ${active ? activeStyle : 'border-slate-200 bg-white text-slate-400 hover:bg-slate-50'}
        disabled:cursor-not-allowed disabled:opacity-50`}
    >
      <span
        className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${active ? (color === 'indigo' ? 'bg-indigo-500' : 'bg-teal-500') : 'bg-slate-300'}`}
      />
      {children}
    </button>
  );
}

/* ─── User detail panel (sites + rules + actions) ────────── */
function UserDetailPanel({ user }: { user: UserDto }) {
  const qc = useQueryClient();
  const actor = useAuthStore((s) => s.user);

  const { data: allSites, isLoading: allSitesLoading } = useQuery<SiteDto[]>({
    queryKey: ['admin-sites'],
    queryFn: fetchSites,
  });

  const { data: userSites, isLoading: userSitesLoading } = useQuery({
    queryKey: ['user-sites', user.id],
    queryFn: () => fetchUserSites(user.id),
  });

  const { data: generalRules, isLoading: rulesLoading } = useQuery({
    queryKey: ['user-general-rules', user.id],
    queryFn: () => fetchUserGeneralRules(user.id),
  });

  const sitesMutKey = ['user-sites', user.id] as const;

  const assignSite = useMutation({
    mutationFn: (siteId: string) => assignUserToSite(user.id, siteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: sitesMutKey }),
  });

  const unassignSite = useMutation({
    mutationFn: (siteId: string) => unassignUserFromSite(user.id, siteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: sitesMutKey }),
  });

  const setPreferred = useMutation({
    mutationFn: (siteId: string) => setPreferredSiteForUser(user.id, siteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: sitesMutKey }),
  });

  const updateRule = useMutation({
    mutationFn: ({ key, value }: { key: GeneralRuleKey; value: boolean }) =>
      updateUserGeneralRules(user.id, { [key]: value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-general-rules', user.id] });
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => disableUser(user.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });

  const enableMutation = useMutation({
    mutationFn: () => enableUser(user.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });

  const assignedSiteIds = new Set(userSites?.map((s) => s.siteId) ?? []);
  const preferredSiteId = userSites?.find((s) => s.isPreferred)?.siteId ?? null;
  const sitesPending = assignSite.isPending || unassignSite.isPending || setPreferred.isPending;
  const isLoading = allSitesLoading || userSitesLoading || rulesLoading;

  if (isLoading) {
    return (
      <div className="border-t border-slate-100 bg-slate-50/60 px-8 py-6 text-sm text-slate-400">
        Chargement…
      </div>
    );
  }

  return (
    <div className="border-t border-slate-100 bg-gradient-to-b from-slate-50/80 to-white px-8 py-6">
      <div className="grid gap-8 sm:grid-cols-2">
        {/* ── Sites ── */}
        <div>
          <p className="mb-3 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-slate-500">
            Sites de travail
          </p>
          <SitePicker
            sites={allSites ?? []}
            selected={assignedSiteIds}
            preferred={preferredSiteId}
            pending={sitesPending}
            onToggle={(siteId) =>
              assignedSiteIds.has(siteId) ? unassignSite.mutate(siteId) : assignSite.mutate(siteId)
            }
            onPreferred={(siteId) => setPreferred.mutate(siteId)}
          />
        </div>

        {/* ── Droits ── */}
        <div>
          <p className="mb-3 text-[0.65rem] font-bold uppercase tracking-[0.15em] text-slate-500">
            Droits généraux
          </p>
          {!generalRules ? (
            <p className="text-sm text-slate-400">Permissions non disponibles.</p>
          ) : (
            <div className="space-y-3">
              {RULE_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="mb-1.5 text-[0.62rem] font-semibold uppercase tracking-wider text-slate-400">
                    {group.label}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {group.keys.map((key) => (
                      <Chip
                        key={key}
                        active={generalRules[key]}
                        disabled={updateRule.isPending}
                        color="indigo"
                        onClick={() => updateRule.mutate({ key, value: !generalRules[key] })}
                      >
                        {RULE_LABELS[key]}
                      </Chip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Actions ── */}
      {actor?.id !== user.id && (
        <div className="mt-6 flex flex-wrap gap-3 border-t border-slate-100 pt-5">
          {user.disabledAt ? (
            <button
              type="button"
              onClick={() => enableMutation.mutate()}
              disabled={enableMutation.isPending}
              className="rounded-xl border border-teal-200 bg-white px-4 py-2 text-xs font-semibold text-teal-700 transition hover:bg-teal-50 disabled:opacity-50"
            >
              Réactiver le compte
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Désactiver ce compte utilisateur ?')) {
                  disableMutation.mutate();
                }
              }}
              disabled={disableMutation.isPending}
              className="rounded-xl border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
            >
              Désactiver le compte
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Main table ──────────────────────────────────────────── */
export function AdminUsersTable() {
  const actor = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => fetchAdminUsers({ page: 1, limit: 100 }),
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserDto['role'] }) =>
      updateUserRole(id, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: USERS_KEY }),
  });

  if (!actor) return null;

  if (isLoading)
    return (
      <div className="rounded-3xl bg-white px-16 py-24 text-center text-sm font-medium text-slate-600 shadow-sm ring-1 ring-slate-200">
        Chargement des utilisateurs…
      </div>
    );

  if (queryError)
    return (
      <div className="rounded-3xl border border-red-200 bg-red-50 px-8 py-16 text-center">
        <p className="font-semibold text-red-950">Impossible de charger les utilisateurs.</p>
        <p className="mt-2 text-sm text-red-600">
          Vérifiez que vous disposez des droits d'administration, puis réessayez.
        </p>
      </div>
    );

  if (!data?.data?.length)
    return (
      <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-8 py-16 text-center text-sm text-slate-600 ring-1 ring-slate-200/70">
        Aucun utilisateur.
      </div>
    );

  return (
    <div className="app-card overflow-hidden">
      <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[700px] table-fixed border-collapse text-left text-[0.9rem]">
          <colgroup>
            <col className="w-[22%]" />
            <col className="w-[28%]" />
            <col className="w-[22%]" />
            <col className="w-[16%]" />
            <col className="w-[12%]" />
          </colgroup>
          <thead className="bg-gradient-to-br from-teal-50 via-white to-slate-50/80 text-[0.65rem] font-bold uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="px-6 py-4 pl-8">Alias</th>
              <th className="px-6 py-4">Nom</th>
              <th className="px-6 py-4">E-mail</th>
              <th className="px-6 py-4">Rôle</th>
              <th className="px-6 py-4 pr-8 text-right">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {data.data.map((user) => {
              const isExpanded = expandedId === user.id;
              const isDisabled = !!user.disabledAt;

              return (
                <React.Fragment key={user.id}>
                  <tr
                    className={`cursor-pointer transition-colors ${isExpanded ? 'bg-teal-50/50' : 'hover:bg-slate-50'}`}
                    tabIndex={0}
                    onClick={() => setExpandedId(isExpanded ? null : user.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId(isExpanded ? null : user.id);
                      }
                    }}
                  >
                    <td className="px-6 py-4 pl-8 font-mono text-[0.8rem] font-semibold text-slate-700">
                      {user.alias}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {user.firstName || user.lastName
                        ? [user.firstName, user.lastName].filter(Boolean).join(' ')
                        : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{user.email ?? '—'}</td>
                    <td
                      className="px-6 py-4"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {actor.role === 'super_admin' && actor.id !== user.id ? (
                        <RoleSelect
                          user={user}
                          currentActorRole={actor.role}
                          currentActorId={actor.id}
                          disabled={updateRole.isPending}
                          onChange={(r) => updateRole.mutate({ id: user.id, role: r })}
                        />
                      ) : (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold
                          ${user.role === 'super_admin' ? 'bg-purple-50 text-purple-700' : user.role === 'admin' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}
                        >
                          {ROLE_LABELS[user.role]}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 pr-8 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span
                          className={`inline-flex h-2 w-2 rounded-full ${isDisabled ? 'bg-red-400' : 'bg-emerald-400'}`}
                        />
                        <span
                          className={`text-xs font-medium ${isDisabled ? 'text-red-600' : 'text-emerald-700'}`}
                        >
                          {isDisabled ? 'Désactivé' : 'Actif'}
                        </span>
                        <svg
                          className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                          aria-hidden
                        >
                          <title>Déplier les détails</title>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={5} className="p-0">
                        <UserDetailPanel user={user} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
